<template>
  <div class="overview-indicator-wrapper">
    <!-- Indicator -->
    <router-link
      activeClass="overview-indicator--active"
      :aria-label="$t('shafts.overview.indicator')"
      :class="['overview-indicator', sizeModifierClass, colorModifierClass]"
      :to="{
        name: 'shaftV2IssuesOverviewGroup',
        params: { setGroupId: data.id },
      }"
      @mouseleave="tooltipVisible = false"
      @mouseover="tooltipVisible = true"
    />
    <!-- Tooltip -->
    <div class="overview-tooltip-container">
      <div
        :aria-label="$t('shafts.overview.tooltip')"
        :class="toolTipClasses"
      >
        {{ data.name }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  /** Data representing a group of sets */
  data: {
    type: Object,
    required: true,
    default: () => {},
    validator(value) {
      return [0, 1, 2, 3, 4, 5].includes(value.highest_condition);
    },
  },
});

const tooltipVisible = ref(false);

/**
 * Logic for working out the size of the indicator.
 * small => issue count is less than or equal to 5
 * medium => issue count is greater than 5 AND less than or equal to 10
 * large => issue count is greater than 11
 * */

/** small => issue count is less than or equal to 5 */
const isLowIssueCount = computed(() => {
  return props.data.issue_count <= 5;
});

/** medium => issue count is greater than 5 AND less than or equal to 10 */
const isMediumIssueCount = computed(() => {
  return props.data.issue_count > 5 && props.data.issue_count <= 10;
});

/** large => issue count is greater than 11 */
const isHighIssueCount = computed(() => {
  return props.data.issue_count >= 11;
});

const sizeModifierClass = computed(() => {
  return {
    'overview-indicator--high': isHighIssueCount.value,
    'overview-indicator--medium': isMediumIssueCount.value,
    'overview-indicator--small': isLowIssueCount.value,
  };
});

const colorModifierClass = computed(() => {
  return `overview-indicator__condition-color--${props.data.highest_condition}`;
});

const toolTipClasses = computed(() => {
  return {
    'overview-tooltip': true,
    'typo-smallish': true,
    'overview-tooltip--active': tooltipVisible.value,
  };
});
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$indicator-high: rem(37);
$indicator-medium: rem(27);
$indicator-default: rem(17);

@mixin indicatorSize($size) {
  width: $size;
  height: $size;
}

.overview-indicator-wrapper {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;
  width: 100%;
}

.overview-indicator {
  @include indicatorSize($indicator-default);
  border-radius: 100%;
  cursor: pointer;
  opacity: 0.55;
  position: relative;
  transition: $transition-duration ease-in-out;

  &:hover {
    opacity: 1;
    transition: $transition-duration ease-in-out;
  }

  @each $condition-rating, $color in $condition-rating-color {
    &__condition-color--#{$condition-rating} {
      // Store current color in variable
      $condtion-color: map-get($color, default);

      background-color: $condtion-color;

      &:before {
        @include centerAbsoluteElement;
        background-color: transparentize($condtion-color, 0.5);
        border-radius: 100%;
        border: 1px solid transparent;
        content: '';
        left: 0;
        top: 0;
        transform-origin: center;
        transform: scale(2);
      }

      &:hover {
        &:before {
          border: 1px solid $condtion-color;
        }
      }
    }
  }

  &--high {
    @include indicatorSize($indicator-high);
  }

  &--medium {
    @include indicatorSize($indicator-medium);
  }

  &--low {
    @include indicatorSize($indicator-default);
  }

  &--active {
    opacity: 1;
    transition: $transition-duration ease-in-out;
    scroll-margin: bu(20);

    @each $condition-rating, $color in $condition-rating-color {
      &.overview-indicator__condition-color--#{$condition-rating} {
        $condtion-color: map-get($color, default);

        &:before {
          border: 1px solid $condtion-color;
        }
      }
    }
  }
}

.overview-tooltip-container {
  bottom: 0;
  position: absolute;
  top: 0;
}

.overview-tooltip {
  @include setOpacityAndVisibility(false);
  background-color: darken(map-get($background-color, darkest), 5%);
  border-radius: map-get($border-radius, radius-8);
  color: map-get($foreground-color, default);
  left: bu(3.75);
  padding: bu(1) bu(2);
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  transition: $transition-duration ease-in-out;
  width: max-content;

  &:before {
    border-bottom: bu(1) solid transparent;
    border-right: bu(1) solid darken(map-get($background-color, darkest), 5%);
    border-top: bu(1) solid transparent;
    content: '';
    left: bu(-1);
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
  }
}

.overview-tooltip--active {
  @include setOpacityAndVisibility;
  left: bu(6);
  transition: 0.2s ease-in-out;
}
</style>
